import { useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  UserRoundCog,
  X,
} from 'lucide-react'
import {
  ApiError,
  applyBulkEdit,
  bulkReassignOpportunities,
  deleteOpportunity,
  fetchAiSystemSettings,
  fetchFinanceConsole,
  fetchFinanceOwners,
  fetchForecastMonthly,
  fetchForecastSummary,
  fetchForecastViews,
  fetchMyAlerts,
  fetchOpportunity,
  fetchOpportunities,
  fetchStages,
  previewBulkEdit,
  restoreOpportunity,
} from '@/api/client'
import type { BulkEditPreviewItem, ForecastMonthMetric, Opportunity } from '@/api/types'
import { canViewAllOpportunities, isFinanceUser, isOwnerUser } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ControlRow } from '@/components/ControlRow'
import { DataTable, TableBody, TableHeader } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { ExportPublishToolbar } from '@/components/ExportPublishToolbar'
import { IconButton } from '@/components/IconButton'
import { HubPage } from '@/components/HubPage'
import { LoadingState } from '@/components/LoadingState'
import { Money } from '@/components/Money'
import { MonthMetricToggle, MonthStrip } from '@/components/MonthStrip'
import { RevenueTrend } from '@/components/RevenueTrend'
import {
  amountsForMetric,
  customerYearTotalForMetric,
  lineYearTotalForMetric,
  opportunityMonthsToForecastAmounts,
  sumLineYearTotalsForMetric,
} from '@/lib/month-strip'
import {
  filterAmountsForQuarter,
  quarterMonthLabels,
  sumMetricForQuarter,
  type ForecastQuarter,
} from '@/lib/quarter'
import { Probability } from '@/components/Probability'
import { Pagination } from '@/components/Pagination'
import { SortableTh, type SortDir } from '@/components/SortableTh'
import { StageBadge } from '@/components/StageBadge'
import { StatTile } from '@/components/StatTile'
import { useAppChrome } from '@/context/AppChromeContext'
import { useFitPageSize } from '@/hooks/useFitPageSize'
import { formatDate } from '@/lib/format-date'
import { FitScalePane } from '@/components/FitScalePane'
import { ChatPanel } from '@/features/ai/ChatPanel'
import { CustomerRevenueBars } from './CustomerRevenueBars'
import { useToast } from '@/components/toast-context'
import { OPPORTUNITY_FORM_ID, OpportunityForm } from './OpportunityForm'
import { groupOpportunitiesByCustomer, shouldGroupByCustomer } from './groupOpportunities'

type ConfirmKind = 'discard' | 'remove' | null
type PendingNavigation = { kind: 'select'; id: string } | { kind: 'create' } | { kind: 'ai' } | null

export function OpportunityListPage() {
  const { user } = useAuth()
  const finance = isFinanceUser(user)
  const ownerOnly = isOwnerUser(user)
  const seesAllOpportunities = canViewAllOpportunities(user)
  const { year, viewCode, quarter } = useAppChrome()
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const aiAssistButtonRef = useRef<HTMLButtonElement>(null)
  const listPaneRef = useRef<HTMLDivElement>(null)

  const [aiAssistOpen, setAiAssistOpen] = useState(false)
  const [aiActivityBadge, setAiActivityBadge] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation>(null)
  const aiAssistOpenRef = useRef(false)
  const dirtyRef = useRef(false)
  const aiSettingsQuery = useQuery({
    queryKey: ['ai', 'settings', 'system'],
    queryFn: fetchAiSystemSettings,
    staleTime: 60_000,
  })
  const aiAssistEnabled = Boolean(
    aiSettingsQuery.data?.ai_enabled && aiSettingsQuery.data?.channels?.chat,
  )
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '')
  const [stageId, setStageId] = useState(() => searchParams.get('stage_id') ?? '')
  const [customerIds, setCustomerIds] = useState<string[]>(() =>
    searchParams.getAll('customer_id').filter(Boolean),
  )
  const [customerFilterNames, setCustomerFilterNames] = useState<Record<string, string>>(() => {
    const ids = searchParams.getAll('customer_id').filter(Boolean)
    const names = searchParams.getAll('customer_name')
    const map: Record<string, string> = {}
    ids.forEach((id, index) => {
      const name = names[index]
      if (name) map[id] = name
    })
    return map
  })
  const [includeZeroYearTotal, setIncludeZeroYearTotal] = useState(
    () => searchParams.get('include_zero') === '1',
  )
  const [showRemoved, setShowRemoved] = useState(() => searchParams.get('removed') === '1')
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(
    () => searchParams.get('attention') === '1',
  )
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('customer_year_total')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [confirmBlocked, setConfirmBlocked] = useState(false)
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [bulkMode, setBulkMode] = useState<'reassign' | 'stage' | null>(null)
  const [reassignOwnerId, setReassignOwnerId] = useState('')
  const [bulkStageId, setBulkStageId] = useState('')
  const [bulkPreview, setBulkPreview] = useState<BulkEditPreviewItem[] | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [monthMetric, setMonthMetric] = useState<ForecastMonthMetric>('weighted')
  const revenueSortColumn: 'year_total' | 'weighted_total' =
    monthMetric === 'weighted' ? 'weighted_total' : 'year_total'
  const revenueColumnLabel =
    monthMetric === 'weighted'
      ? 'Weighted revenue'
      : monthMetric === 'committed'
        ? 'Committed revenue'
        : 'Upside revenue'

  const [pageSize, setPageSizeState] = useState(8)
  const packByCustomerLines = sortBy === 'customer_year_total'

  const query = useMemo(() => {
    const base = {
      year,
      view: viewCode,
      search: search || undefined,
      stage_id: stageId || undefined,
      customer_id: customerIds.length > 0 ? customerIds : undefined,
      sort_by: sortBy,
      sort_dir: sortDir,
      include_zero_year_total: includeZeroYearTotal || undefined,
      exclude_removed: showRemoved ? false : undefined,
    }
    if (packByCustomerLines) {
      // skip = packed page index; max_lines = opportunity-line budget (≤20).
      return { ...base, max_lines: pageSize, skip: page - 1, limit: 50 }
    }
    return {
      ...base,
      limit: pageSize,
      skip: (page - 1) * pageSize,
    }
  }, [
    year,
    viewCode,
    search,
    stageId,
    customerIds,
    sortBy,
    sortDir,
    includeZeroYearTotal,
    showRemoved,
    page,
    pageSize,
    packByCustomerLines,
  ])

  const listQuery = useQuery({
    queryKey: ['opportunities', query],
    queryFn: () => fetchOpportunities(query),
    // Keep prior page totals while the next page loads — otherwise total→0 clamps page to 1.
    placeholderData: keepPreviousData,
  })

  const summaryQuery = useQuery({
    queryKey: ['forecast-summary', year, viewCode],
    queryFn: () => fetchForecastSummary(year, viewCode),
  })

  const monthlyQuery = useQuery({
    queryKey: ['forecast-monthly', year, viewCode],
    queryFn: () => fetchForecastMonthly(year, viewCode),
  })

  const stagesQuery = useQuery({
    queryKey: ['config', 'stages'],
    queryFn: () => fetchStages(),
  })

  const viewsQuery = useQuery({
    queryKey: ['config', 'views'],
    queryFn: fetchForecastViews,
    enabled: finance,
  })

  const ownersQuery = useQuery({
    queryKey: ['finance', 'owners'],
    queryFn: () => fetchFinanceOwners(),
    enabled: finance,
  })

  const consoleQuery = useQuery({
    queryKey: ['finance', 'console'],
    queryFn: fetchFinanceConsole,
    enabled: finance,
    staleTime: 30_000,
  })

  const detailQuery = useQuery({
    queryKey: ['opportunity', selectedId, year],
    queryFn: () => fetchOpportunity(selectedId as string, year),
    enabled: Boolean(selectedId) && !creating,
  })

  const alertsQuery = useQuery({
    queryKey: ['alerts', 'mine'],
    queryFn: fetchMyAlerts,
    enabled: Boolean(user),
  })

  const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data?.items])
  const total = listQuery.data?.total ?? 0
  const pageUnit =
    listQuery.data?.page_unit ?? (sortBy === 'customer_year_total' ? 'customer' : 'item')
  /** Gate on API page_unit so keepPreviousData cannot paint orphan subtotals. */
  const customerGrouped = shouldGroupByCustomer({
    sortBy,
    pageUnit: listQuery.data?.page_unit,
  })
  const weeklyReviewDue = useMemo(
    () => (alertsQuery.data ?? []).some((alert) => alert.rule_code === 'weekly_prompt'),
    [alertsQuery.data],
  )
  const needsAttentionCount = useMemo(() => {
    if (finance && consoleQuery.data?.hygiene) {
      return consoleQuery.data.hygiene.length
    }
    return items.filter(
      (item) => item.hygiene_status === 'warning' || item.hygiene_status === 'error',
    ).length
  }, [finance, consoleQuery.data?.hygiene, items])

  const visibleItems = useMemo(() => {
    if (!needsAttentionOnly) return items
    return items.filter(
      (item) => item.hygiene_status === 'warning' || item.hygiene_status === 'error',
    )
  }, [items, needsAttentionOnly])

  const groupedByCustomer = useMemo(() => {
    if (!customerGrouped) return null
    return groupOpportunitiesByCustomer(visibleItems, sortDir)
  }, [visibleItems, customerGrouped, sortDir])

  const fittedPageSize = useFitPageSize({
    containerRef: listPaneRef,
    // Size by sort intent; group chrome still waits on page_unit (see customerGrouped).
    customerGrouped: sortBy === 'customer_year_total',
  })

  useEffect(() => {
    if (fittedPageSize !== pageSize) {
      setPageSizeState(fittedPageSize)
    }
  }, [fittedPageSize, pageSize])

  // Clamp only against settled totals — placeholder totals use the previous page_unit
  // and will yank the page when limit flips between customers and items.
  useEffect(() => {
    if (listQuery.isPlaceholderData) return
    const packedPages = listQuery.data?.total_pages
    if (packedPages != null) {
      setPage((current) => (current > packedPages ? Math.max(1, packedPages) : current))
      return
    }
    if (total <= 0) return
    const maxPage = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)))
    setPage((current) => (current > maxPage ? maxPage : current))
  }, [pageSize, total, listQuery.isPlaceholderData, listQuery.data?.total_pages])
  const detailOpen = creating || Boolean(selectedId)
  const editing = detailQuery.data ?? null
  const listSelected = selectedId ? items.find((item) => item.id === selectedId) : undefined
  const selectedCount = selectedIds.size
  const allVisibleSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id))
  const tableColumns = (finance ? 1 : 0) + (customerGrouped ? 4 : 5)
  const pageTitle = seesAllOpportunities ? 'All opportunities' : 'My opportunities'
  const pageSubtitle = finance
    ? `Finance sees every line. Filter by stage and search · ${year}`
    : ownerOnly && user?.is_super_owner
      ? 'You can view every line; edit only the ones you own.'
      : ownerOnly
        ? 'You can view and edit only the lines you own.'
        : listQuery.data
          ? `${listQuery.data.total} ${pageUnit === 'customer' ? 'customers' : 'lines'} · ${year}`
          : '…'

  function onSort(column: string) {
    if (sortBy === column) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      const revenueFirst =
        column === 'customer_year_total' || column === 'year_total' || column === 'weighted_total'
      setSortDir(revenueFirst ? 'desc' : 'asc')
    }
    setPage(1)
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const item of items) next.delete(item.id)
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const item of items) next.add(item.id)
        return next
      })
    }
  }

  function closeBulkPanels() {
    setBulkMode(null)
    setBulkPreview(null)
    setBulkError(null)
    setReassignOwnerId('')
    setBulkStageId('')
  }

  async function runBulkReassign() {
    if (!reassignOwnerId || selectedCount === 0) return
    setBulkBusy(true)
    setBulkError(null)
    try {
      await bulkReassignOpportunities({
        owner_id: reassignOwnerId,
        opportunity_ids: [...selectedIds],
      })
      setSelectedIds(new Set())
      closeBulkPanels()
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    } catch (err) {
      setBulkError(err instanceof ApiError ? err.message : 'Bulk reassign failed.')
    } finally {
      setBulkBusy(false)
    }
  }

  async function runBulkStagePreview() {
    if (!bulkStageId || selectedCount === 0) return
    setBulkBusy(true)
    setBulkError(null)
    try {
      const preview = await previewBulkEdit(
        { opportunity_ids: [...selectedIds], stage_id: bulkStageId },
        year,
      )
      setBulkPreview(preview.items)
    } catch (err) {
      setBulkError(err instanceof ApiError ? err.message : 'Preview failed.')
    } finally {
      setBulkBusy(false)
    }
  }

  async function confirmBulkStageEdit() {
    if (!bulkStageId || selectedCount === 0) return
    setBulkBusy(true)
    setBulkError(null)
    try {
      await applyBulkEdit({
        opportunity_ids: [...selectedIds],
        stage_id: bulkStageId,
      })
      setSelectedIds(new Set())
      closeBulkPanels()
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      void queryClient.invalidateQueries({ queryKey: ['forecast-summary'] })
      void queryClient.invalidateQueries({ queryKey: ['forecast-monthly'] })
      void queryClient.invalidateQueries({ queryKey: ['opportunity'] })
    } catch (err) {
      setBulkError(err instanceof ApiError ? err.message : 'Bulk edit failed.')
    } finally {
      setBulkBusy(false)
    }
  }

  useEffect(() => {
    const selected = searchParams.get('selected')
    if (selected) {
      setAiAssistOpen(false)
      setSelectedId(selected)
      setCreating(false)
      setSearchParams(
        (prev) => {
          const copy = new URLSearchParams(prev)
          copy.delete('selected')
          return copy
        },
        { replace: true },
      )
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const copy = new URLSearchParams(prev)
        if (search) copy.set('search', search)
        else copy.delete('search')
        if (stageId) copy.set('stage_id', stageId)
        else copy.delete('stage_id')
        copy.delete('customer_id')
        copy.delete('customer_name')
        for (const id of customerIds) {
          copy.append('customer_id', id)
          // Keep names aligned 1:1 with ids for round-trip restore.
          copy.append('customer_name', customerFilterNames[id] ?? '')
        }
        if (includeZeroYearTotal) copy.set('include_zero', '1')
        else copy.delete('include_zero')
        if (showRemoved) copy.set('removed', '1')
        else copy.delete('removed')
        if (needsAttentionOnly) copy.set('attention', '1')
        else copy.delete('attention')
        return copy
      },
      { replace: true },
    )
  }, [
    search,
    stageId,
    customerIds,
    customerFilterNames,
    includeZeroYearTotal,
    showRemoved,
    needsAttentionOnly,
    setSearchParams,
  ])

  useEffect(() => {
    setPage(1)
  }, [search, stageId, customerIds, includeZeroYearTotal, showRemoved, needsAttentionOnly])

  useEffect(() => {
    setSortBy((prev) => {
      if (prev === 'year_total' || prev === 'weighted_total') {
        return monthMetric === 'weighted' ? 'weighted_total' : 'year_total'
      }
      return prev
    })
  }, [monthMetric])

  useEffect(() => {
    setBulkPreview(null)
  }, [selectedIds])

  function refreshHubData() {
    void listQuery.refetch()
    void summaryQuery.refetch()
    void monthlyQuery.refetch()
  }

  function setCustomerSelection(customers: Array<{ id: string; name: string }>) {
    setCustomerIds(customers.map((c) => c.id))
    setCustomerFilterNames(Object.fromEntries(customers.map((c) => [c.id, c.name] as const)))
    setSelectedId(null)
    setCreating(false)
    setDirty(false)
    setPage(1)
  }

  function clearCustomerFilter() {
    setCustomerIds([])
    setCustomerFilterNames({})
  }

  useEffect(() => {
    if (customerIds.length === 0 || !monthlyQuery.data) return
    setCustomerFilterNames((prev) => {
      let changed = false
      const next = { ...prev }
      for (const id of customerIds) {
        if (next[id]) continue
        const match = monthlyQuery.data.customers.find((c) => c.customer_id === id)
        if (match) {
          next[id] = match.customer_name
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [customerIds, monthlyQuery.data])

  aiAssistOpenRef.current = aiAssistOpen
  dirtyRef.current = dirty

  /** §4.1 A — exclusive right surface: AI vs opportunity detail. */
  function openAiAssist() {
    if (dirtyRef.current) {
      setPendingNavigation({ kind: 'ai' })
      setConfirmKind('discard')
      return
    }
    setCreating(false)
    setSelectedId(null)
    setAiActivityBadge(false)
    setAiAssistOpen(true)
  }

  function closeAiAssist() {
    setAiAssistOpen(false)
    window.setTimeout(() => aiAssistButtonRef.current?.focus(), 0)
  }

  function toggleAiAssist() {
    if (aiAssistOpenRef.current) closeAiAssist()
    else openAiAssist()
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        if (!aiAssistEnabled || !(finance || ownerOnly)) return
        event.preventDefault()
        if (aiAssistOpenRef.current) {
          setAiAssistOpen(false)
          window.setTimeout(() => aiAssistButtonRef.current?.focus(), 0)
        } else if (dirtyRef.current) {
          setPendingNavigation({ kind: 'ai' })
          setConfirmKind('discard')
        } else {
          setCreating(false)
          setSelectedId(null)
          setAiActivityBadge(false)
          setAiAssistOpen(true)
        }
        return
      }

      if (event.key !== '/' || inField) return
      event.preventDefault()
      searchInputRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [aiAssistEnabled, finance, ownerOnly])

  function finishCloseDetail() {
    setCreating(false)
    setSelectedId(null)
    setDirty(false)
    setConfirmKind(null)
  }

  function closeDetail() {
    if (dirty) {
      setConfirmKind('discard')
      return
    }
    finishCloseDetail()
  }

  function startCreate() {
    if (dirty) {
      setPendingNavigation({ kind: 'create' })
      setConfirmKind('discard')
      return
    }
    setAiAssistOpen(false)
    setSelectedId(null)
    setCreating(true)
  }

  function selectRow(id: string) {
    if (id === selectedId && !creating) return
    if (dirty) {
      setPendingNavigation({ kind: 'select', id })
      setConfirmKind('discard')
      return
    }
    setAiAssistOpen(false)
    setCreating(false)
    setSelectedId(id)
  }

  function openOpportunityFromAi(opportunityId: string) {
    setAiAssistOpen(false)
    setCreating(false)
    setSelectedId(opportunityId)
    setDirty(false)
  }

  function afterSave() {
    finishCloseDetail()
    setConfirmBlocked(false)
    void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    void queryClient.invalidateQueries({ queryKey: ['opportunity'] })
    void queryClient.invalidateQueries({ queryKey: ['forecast-summary'] })
    void queryClient.invalidateQueries({ queryKey: ['forecast-monthly'] })
    void queryClient.invalidateQueries({ queryKey: ['finance', 'console'] })
  }

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteOpportunity(id),
    onSuccess: () => {
      afterSave()
    },
    onError: (caught) => {
      const message = caught instanceof ApiError ? caught.message : 'Could not remove opportunity.'
      toast.error(message)
    },
  })

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreOpportunity(id),
    onSuccess: () => {
      toast.success('Opportunity restored.')
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      void queryClient.invalidateQueries({ queryKey: ['forecast-summary'] })
      void queryClient.invalidateQueries({ queryKey: ['forecast-monthly'] })
    },
    onError: (caught) => {
      const message = caught instanceof ApiError ? caught.message : 'Could not restore opportunity.'
      toast.error(message)
    },
  })

  function handleRemove() {
    if (!selectedId || !editing) return
    setConfirmKind('remove')
  }

  function handleConfirmDialogConfirm() {
    if (confirmKind === 'discard') {
      const pending = pendingNavigation
      setPendingNavigation(null)
      finishCloseDetail()
      if (pending?.kind === 'ai') {
        setAiActivityBadge(false)
        setAiAssistOpen(true)
      } else if (pending?.kind === 'create') {
        setAiAssistOpen(false)
        setSelectedId(null)
        setCreating(true)
      } else if (pending?.kind === 'select') {
        setAiAssistOpen(false)
        setCreating(false)
        setSelectedId(pending.id)
      }
      return
    }
    if (confirmKind === 'remove' && selectedId) {
      removeMutation.mutate(selectedId)
      setConfirmKind(null)
    }
  }

  const detailTitle = creating
    ? 'New opportunity'
    : `Edit ${editing?.name ?? listSelected?.name ?? 'opportunity'}`
  const removeLabel = editing?.name || listSelected?.name || 'this opportunity'

  const showDetailForm = creating || (Boolean(selectedId) && Boolean(editing))
  const showDetailLoading = Boolean(selectedId) && !creating && detailQuery.isLoading
  const showDetailError = Boolean(
    selectedId && !creating && detailQuery.error && !detailQuery.isLoading,
  )

  return (
    <>
      <HubPage
        chrome={
          <>
            <ControlRow
              sticky={false}
              leading={
                <>
                  <h1 className="page__title">{pageTitle}</h1>
                  <p className="page__subtitle">{pageSubtitle}</p>
                </>
              }
              actions={
                <>
                  <IconButton label="Refresh list" variant="ghost" onClick={refreshHubData}>
                    <RefreshCw size={18} strokeWidth={2} />
                  </IconButton>
                  {(finance || ownerOnly) && aiAssistEnabled && (
                    <span className="ai-assist-entry">
                      <IconButton
                        ref={aiAssistButtonRef}
                        label={aiAssistOpen ? 'Close AI assist (⌘J)' : 'Open AI assist (⌘J)'}
                        variant="ghost"
                        active={aiAssistOpen}
                        onClick={toggleAiAssist}
                      >
                        <Sparkles size={18} strokeWidth={2} />
                      </IconButton>
                      {!aiAssistOpen && aiActivityBadge ? (
                        <span className="ai-assist-entry__badge" aria-hidden />
                      ) : null}
                    </span>
                  )}
                  <button type="button" className="button button--primary" onClick={startCreate}>
                    <Plus size={16} strokeWidth={2} aria-hidden />
                    New opportunity
                  </button>
                  {finance && (
                    <ExportPublishToolbar
                      views={viewsQuery.data ?? []}
                      onPublished={() => {
                        void summaryQuery.refetch()
                        void listQuery.refetch()
                      }}
                    />
                  )}
                </>
              }
            >
              <input
                ref={searchInputRef}
                className="field__input field__input--compact toolbar__search"
                type="search"
                placeholder="Search customer or project…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Search opportunities"
              />
              <select
                className="field__input field__input--compact"
                value={stageId}
                onChange={(event) => setStageId(event.target.value)}
                aria-label="Filter by stage"
              >
                <option value="">All stages</option>
                {(stagesQuery.data ?? []).map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
              <label className="field field--checkbox field--inline toolbar__zero-toggle">
                <input
                  type="checkbox"
                  checked={includeZeroYearTotal}
                  onChange={(event) => setIncludeZeroYearTotal(event.target.checked)}
                />
                <span className="field__label">Show $0 year total</span>
              </label>
              <label className="field field--checkbox field--inline toolbar__zero-toggle">
                <input
                  type="checkbox"
                  checked={showRemoved}
                  onChange={(event) => setShowRemoved(event.target.checked)}
                />
                <span className="field__label">Show removed</span>
              </label>
              {customerIds.length > 0 && (
                <button
                  type="button"
                  className="filter-chip"
                  onClick={clearCustomerFilter}
                  aria-label={`Clear customer filter: ${
                    customerIds.length === 1
                      ? customerFilterNames[customerIds[0]!] || 'selected'
                      : `${customerIds.length} customers`
                  }`}
                >
                  <span className="filter-chip__label">
                    {customerIds.length === 1
                      ? `Customer: ${customerFilterNames[customerIds[0]!] || 'selected'}`
                      : `Customers: ${customerIds.length} selected`}
                  </span>
                  <X size={14} aria-hidden strokeWidth={2} />
                </button>
              )}
            </ControlRow>

            {weeklyReviewDue && (
              <div className="review-banner" role="status">
                <p className="review-banner__text">
                  Weekly review is due by end of Tuesday (IST). Check your lines and confirm.
                </p>
                <Link to="/alerts" className="button button--secondary">
                  Review and confirm
                </Link>
              </div>
            )}

            {(summaryQuery.data || monthlyQuery.data) && (
              <div className="stat-tiles stat-tiles--compact">
                <StatTile
                  label="Committed"
                  tone="success"
                  hint={
                    quarter === 'all'
                      ? 'Unweighted year revenue in committed stages'
                      : `Unweighted Q${quarter} revenue in committed stages`
                  }
                  value={
                    <Money
                      amount={
                        quarter === 'all'
                          ? (summaryQuery.data?.committed_total ?? 0)
                          : sumMetricForQuarter(monthlyQuery.data?.months, 'committed', quarter)
                      }
                      currency={summaryQuery.data?.currency ?? monthlyQuery.data?.currency ?? 'USD'}
                    />
                  }
                />
                <StatTile
                  label="Weighted"
                  tone="primary"
                  hint={
                    quarter === 'all'
                      ? 'Year revenue × stage probability (lost excluded)'
                      : `Q${quarter} revenue × stage probability (lost excluded)`
                  }
                  value={
                    <Money
                      amount={
                        quarter === 'all'
                          ? (summaryQuery.data?.weighted_total ?? 0)
                          : sumMetricForQuarter(monthlyQuery.data?.months, 'weighted', quarter)
                      }
                      currency={summaryQuery.data?.currency ?? monthlyQuery.data?.currency ?? 'USD'}
                    />
                  }
                />
                <StatTile
                  label="Upside"
                  tone="warning"
                  hint={
                    quarter === 'all'
                      ? 'Unweighted year revenue for all non-lost stages'
                      : `Unweighted Q${quarter} revenue for all non-lost stages`
                  }
                  value={
                    <Money
                      amount={
                        quarter === 'all'
                          ? (summaryQuery.data?.upside_total ?? 0)
                          : sumMetricForQuarter(monthlyQuery.data?.months, 'upside', quarter)
                      }
                      currency={summaryQuery.data?.currency ?? monthlyQuery.data?.currency ?? 'USD'}
                    />
                  }
                />
                <StatTile
                  label="Needs attention"
                  tone={needsAttentionCount > 0 ? 'warning' : 'default'}
                  hint={
                    finance
                      ? 'Open Finance console Hygiene to review and jump to each case'
                      : 'Filter this list to hygiene warning/error lines on the current page'
                  }
                  value={needsAttentionCount}
                  active={needsAttentionOnly}
                  onClick={() => {
                    if (finance) {
                      const params = new URLSearchParams()
                      params.set('segment', 'hygiene')
                      params.set('year', String(year))
                      params.set('view', viewCode)
                      if (quarter !== 'all') params.set('quarter', String(quarter))
                      navigate(`/console?${params.toString()}`)
                      return
                    }
                    setNeedsAttentionOnly((prev) => !prev)
                  }}
                />
              </div>
            )}

            {monthlyQuery.data && (
              <div className="portfolio-month-strip">
                <div className="portfolio-month-strip__header">
                  <div className="portfolio-month-strip__copy">
                    <h3 className="portfolio-month-strip__title">Month on month</h3>
                    <p className="portfolio-month-strip__hint">
                      Portfolio {year}
                      {quarter === 'all' ? '' : ` · Q${quarter}`} ·{' '}
                      <Link to="/forecast" className="portfolio-month-strip__link">
                        Full monthly matrix
                      </Link>
                    </p>
                  </div>
                  <div className="portfolio-month-strip__actions">
                    <RevenueTrend
                      amounts={filterAmountsForQuarter(
                        amountsForMetric(monthlyQuery.data.months, monthMetric),
                        quarter,
                      )}
                      currency={monthlyQuery.data.currency}
                      aria-label={`Portfolio ${monthMetric} trend`}
                    />
                    <MonthMetricToggle
                      value={monthMetric}
                      onChange={setMonthMetric}
                      idPrefix="hub-month-metric"
                    />
                  </div>
                </div>
                <MonthStrip
                  density="compact"
                  labels={quarterMonthLabels(quarter)}
                  amounts={filterAmountsForQuarter(
                    amountsForMetric(monthlyQuery.data.months, monthMetric),
                    quarter,
                  )}
                  currency={monthlyQuery.data.currency}
                  aria-label={`Portfolio ${monthMetric} by month`}
                />
              </div>
            )}
          </>
        }
        footer={
          <>
            {finance && selectedCount > 0 && (
              <div className="bulk-bar" role="region" aria-label="Bulk actions">
                <span className="bulk-bar__count">{selectedCount} selected</span>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => {
                    closeBulkPanels()
                    setBulkMode('reassign')
                  }}
                >
                  <UserRoundCog size={16} aria-hidden />
                  Reassign
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => {
                    closeBulkPanels()
                    setBulkMode('stage')
                  }}
                >
                  <Pencil size={16} aria-hidden />
                  Edit stage
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => {
                    setSelectedIds(new Set())
                    closeBulkPanels()
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            {finance && bulkMode === 'reassign' && selectedCount > 0 && (
              <div className="bulk-panel">
                <label className="field">
                  <span className="field__label">New owner</span>
                  <select
                    className="field__input"
                    value={reassignOwnerId}
                    onChange={(event) => setReassignOwnerId(event.target.value)}
                  >
                    <option value="">Choose owner…</option>
                    {(ownersQuery.data ?? []).map((owner) => (
                      <option key={owner.id} value={owner.id}>
                        {owner.full_name} ({owner.email})
                      </option>
                    ))}
                  </select>
                </label>
                <div className="bulk-panel__actions">
                  <button type="button" className="button button--ghost" onClick={closeBulkPanels}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={!reassignOwnerId || bulkBusy}
                    onClick={() => void runBulkReassign()}
                  >
                    Reassign {selectedCount} {selectedCount === 1 ? 'line' : 'lines'} (all years)
                  </button>
                </div>
              </div>
            )}

            {finance && bulkMode === 'stage' && selectedCount > 0 && (
              <div className="bulk-panel">
                <label className="field">
                  <span className="field__label">Stage</span>
                  <select
                    className="field__input"
                    value={bulkStageId}
                    onChange={(event) => {
                      setBulkStageId(event.target.value)
                      setBulkPreview(null)
                    }}
                  >
                    <option value="">Choose stage…</option>
                    {(stagesQuery.data ?? []).map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </label>
                {bulkPreview && bulkPreview.length > 0 && (
                  <div className="table-wrap">
                    <table className="table table--dense">
                      <thead>
                        <tr>
                          <th scope="col">Line</th>
                          <th scope="col" className="table__num">
                            Weighted before
                          </th>
                          <th scope="col" className="table__num">
                            Weighted after
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreview.map((row) => (
                          <tr key={row.opportunity_id}>
                            <td>{row.opportunity_name}</td>
                            <td className="table__num">
                              <Money
                                amount={row.totals.before_weighted}
                                currency={row.totals.currency}
                              />
                            </td>
                            <td className="table__num">
                              <Money
                                amount={row.totals.after_weighted}
                                currency={row.totals.currency}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="bulk-panel__actions">
                  <button type="button" className="button button--ghost" onClick={closeBulkPanels}>
                    Cancel
                  </button>
                  {!bulkPreview ? (
                    <button
                      type="button"
                      className="button button--primary"
                      disabled={!bulkStageId || bulkBusy}
                      onClick={() => void runBulkStagePreview()}
                    >
                      Preview impact
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="button button--primary"
                      disabled={bulkBusy}
                      onClick={() => void confirmBulkStageEdit()}
                    >
                      Confirm bulk edit
                    </button>
                  )}
                </div>
              </div>
            )}

            {bulkError && (
              <div className="alert alert--error" role="alert">
                {bulkError}
              </div>
            )}
          </>
        }
      >
        <div className="split-pane">
          <div className="split-pane__list" data-fit-page="">
            <div className="split-pane__list-main" ref={listPaneRef}>
              {listQuery.isLoading && (
                <LoadingState
                  label="Loading opportunities…"
                  variant="skeleton-table"
                  rows={8}
                  columns={tableColumns}
                />
              )}

              {listQuery.error && (
                <ErrorState
                  message={
                    listQuery.error instanceof ApiError
                      ? listQuery.error.message
                      : 'Could not load opportunities.'
                  }
                  onRetry={() => void listQuery.refetch()}
                />
              )}

              {!listQuery.isLoading && !listQuery.error && items.length === 0 && (
                <EmptyState
                  title="No opportunities match these filters."
                  description="Try a different search or stage filter, or create a new line."
                  action={
                    <button type="button" className="button button--primary" onClick={startCreate}>
                      <Plus size={16} aria-hidden />
                      Create opportunity
                    </button>
                  }
                />
              )}

              {!listQuery.isLoading && !listQuery.error && items.length > 0 && (
                <DataTable
                  fill
                  clickable
                  loadingColumns={tableColumns}
                  className={`table--opp-list${finance ? ' table--opp-list--finance' : ''}${customerGrouped ? ' table--opp-list--grouped' : ''}`}
                >
                  <TableHeader sticky>
                    <tr>
                      {finance && (
                        <th scope="col" className="table__check-col">
                          <input
                            type="checkbox"
                            aria-label="Select all visible rows"
                            checked={allVisibleSelected}
                            onChange={toggleSelectAllVisible}
                          />
                        </th>
                      )}
                      {customerGrouped ? (
                        <th
                          scope="col"
                          className="table__sticky-col opp-col-opportunity"
                          aria-sort={
                            sortBy === 'customer_year_total' || sortBy === 'name'
                              ? sortDir === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                          }
                        >
                          <div className="opp-dual-sort">
                            <SortButton
                              label="Customer"
                              column="customer_year_total"
                              sortBy={sortBy}
                              sortDir={sortDir}
                              onSort={onSort}
                            />
                            <SortButton
                              label="Opportunity"
                              column="name"
                              sortBy={sortBy}
                              sortDir={sortDir}
                              onSort={onSort}
                            />
                          </div>
                        </th>
                      ) : (
                        <>
                          <SortableTh
                            label="Customer"
                            column="customer_year_total"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                            className="table__sticky-col opp-col-customer"
                          />
                          <SortableTh
                            label="Opportunity"
                            column="name"
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSort={onSort}
                            className="table__sticky-col-2 opp-col-opportunity"
                          />
                        </>
                      )}
                      <SortableTh
                        label="Stage"
                        column="stage_name"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={onSort}
                        className="opp-col-stage"
                      />
                      <SortableTh
                        label="Term"
                        column="start_date"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={onSort}
                        className="opp-col-term"
                      />
                      <SortableTh
                        label={revenueColumnLabel}
                        column={revenueSortColumn}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={onSort}
                        numeric
                        className="opp-col-revenue"
                      />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {groupedByCustomer
                      ? groupedByCustomer.flatMap((group) => {
                          const customerRollup = monthlyQuery.data?.customers.find(
                            (c) => c.customer_id === group.customerId,
                          )
                          const selectedInGroup =
                            selectedId != null && group.items.some((line) => line.id === selectedId)
                          const selectedItem =
                            selectedInGroup && selectedId
                              ? group.items.find((line) => line.id === selectedId)
                              : undefined
                          const projectSelected =
                            selectedItem != null && selectedId != null && editing?.id === selectedId
                          const groupMonthAmountsFull = projectSelected
                            ? amountsForMetric(
                                opportunityMonthsToForecastAmounts(
                                  editing?.months,
                                  year,
                                  Number(selectedItem.probability),
                                  selectedItem.stage_category,
                                ),
                                monthMetric,
                              )
                            : amountsForMetric(customerRollup?.months, monthMetric)
                          const groupMonthAmounts = filterAmountsForQuarter(
                            groupMonthAmountsFull,
                            quarter,
                          )
                          const groupMonthLabel = projectSelected
                            ? `${selectedItem.name} ${monthMetric} by month`
                            : `${group.customerName} ${monthMetric} by month`
                          const customerTotal =
                            quarter === 'all'
                              ? (customerYearTotalForMetric(customerRollup, monthMetric) ??
                                sumLineYearTotalsForMetric(group.items, monthMetric))
                              : sumMetricForQuarter(customerRollup?.months, monthMetric, quarter)
                          return [
                            <tr key={`hdr-${group.customerId}`} className="table__group-header">
                              <td colSpan={tableColumns}>
                                <div className="opp-group__header">
                                  <span className="opp-group__name">{group.customerName}</span>
                                  <span className="opp-group__meta">
                                    {group.items.length} line{group.items.length === 1 ? '' : 's'}
                                  </span>
                                  <span className="opp-group__header-total">
                                    <Money
                                      amount={customerTotal}
                                      currency={group.items[0]?.currency ?? 'USD'}
                                    />
                                  </span>
                                </div>
                              </td>
                            </tr>,
                            monthlyQuery.data ? (
                              <tr
                                key={`months-${group.customerId}`}
                                className="table__group-months-row"
                              >
                                <td colSpan={tableColumns}>
                                  <MonthStrip
                                    className="opp-group__months opp-group__months--full"
                                    density="compact"
                                    labels={quarterMonthLabels(quarter)}
                                    amounts={groupMonthAmounts}
                                    currency={monthlyQuery.data.currency}
                                    aria-label={groupMonthLabel}
                                  />
                                </td>
                              </tr>
                            ) : null,
                            ...group.items.map((item) => (
                              <OpportunityRow
                                key={item.id}
                                item={item}
                                active={item.id === selectedId}
                                finance={finance}
                                selected={selectedIds.has(item.id)}
                                omitCustomer
                                monthMetric={monthMetric}
                                year={year}
                                quarter={quarter}
                                showRemoved={showRemoved}
                                onRestore={
                                  showRemoved ? (id) => restoreMutation.mutate(id) : undefined
                                }
                                onToggleSelected={() => toggleSelected(item.id)}
                                onSelect={() => selectRow(item.id)}
                              />
                            )),
                            <tr key={`sub-${group.customerId}`} className="table__group-footer">
                              {finance ? <td /> : null}
                              {/* Opportunity + Stage + Term */}
                              <td className="table__sticky-col opp-col-opportunity" colSpan={3}>
                                <div className="opp-group__subtotal">
                                  <span className="opp-group__subtotal-label">
                                    Customer subtotal
                                  </span>
                                  {monthlyQuery.data && (
                                    <RevenueTrend
                                      className="opp-group__subtotal-trend"
                                      density="compact"
                                      amounts={filterAmountsForQuarter(
                                        amountsForMetric(
                                          monthlyQuery.data.customers.find(
                                            (c) => c.customer_id === group.customerId,
                                          )?.months,
                                          monthMetric,
                                        ),
                                        quarter,
                                      )}
                                      currency={monthlyQuery.data.currency}
                                      aria-label={`${group.customerName} ${monthMetric} trend`}
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="table__num opp-col-revenue">
                                <Money
                                  amount={
                                    quarter === 'all'
                                      ? (customerYearTotalForMetric(
                                          monthlyQuery.data?.customers.find(
                                            (c) => c.customer_id === group.customerId,
                                          ),
                                          monthMetric,
                                        ) ?? sumLineYearTotalsForMetric(group.items, monthMetric))
                                      : monthlyQuery.data
                                        ? sumMetricForQuarter(
                                            monthlyQuery.data.customers.find(
                                              (c) => c.customer_id === group.customerId,
                                            )?.months,
                                            monthMetric,
                                            quarter,
                                          )
                                        : group.items.reduce(
                                            (sum, item) =>
                                              sum +
                                              lineRevenueForMetric(
                                                item,
                                                monthMetric,
                                                year,
                                                quarter,
                                              ),
                                            0,
                                          )
                                  }
                                  currency={group.items[0]?.currency ?? 'USD'}
                                />
                              </td>
                            </tr>,
                          ].filter(Boolean)
                        })
                      : visibleItems.map((item) => (
                          <OpportunityRow
                            key={item.id}
                            item={item}
                            active={item.id === selectedId}
                            finance={finance}
                            selected={selectedIds.has(item.id)}
                            monthMetric={monthMetric}
                            year={year}
                            quarter={quarter}
                            showRemoved={showRemoved}
                            onRestore={showRemoved ? (id) => restoreMutation.mutate(id) : undefined}
                            onToggleSelected={() => toggleSelected(item.id)}
                            onSelect={() => selectRow(item.id)}
                          />
                        ))}
                  </TableBody>
                </DataTable>
              )}
            </div>

            {!listQuery.isLoading && listQuery.data != null && (
              <div className="split-pane__list-footer">
                <Pagination
                  page={page}
                  limit={packByCustomerLines ? Math.max(1, listQuery.data.limit || 1) : pageSize}
                  total={total}
                  onPageChange={setPage}
                  unitLabel={pageUnit === 'customer' ? 'customers' : 'opportunities'}
                  totalPages={
                    packByCustomerLines ? (listQuery.data.total_pages ?? undefined) : undefined
                  }
                  rangeStart={
                    packByCustomerLines && listQuery.data.customer_offset != null
                      ? listQuery.data.customer_offset + 1
                      : undefined
                  }
                  rangeEnd={
                    packByCustomerLines && listQuery.data.customer_offset != null
                      ? listQuery.data.customer_offset + (listQuery.data.limit || 0)
                      : undefined
                  }
                />
                <p className="list-footnote">
                  Probability follows the stage. Amounts shown in whole USD. Lost lines stay visible
                  but contribute $0 to Committed, Weighted, and Upside (matching portfolio totals).
                  {quarter !== 'all'
                    ? ` Tiles, strips, and customer subtotals are scoped to Q${quarter}; opportunity form grids stay full year.`
                    : null}
                  {needsAttentionOnly
                    ? ' Showing hygiene flags on this page only — Finance can open the console for the full list.'
                    : null}
                </p>
              </div>
            )}
          </div>

          <div className="split-pane__detail">
            <FitScalePane
              className="split-pane__detail-fit"
              modeKey={detailOpen ? `detail:${selectedId ?? 'new'}` : 'chart'}
              minScale={detailOpen ? 0.78 : 0.72}
            >
              {!detailOpen ? (
                <div className="split-pane__detail-body split-pane__detail-body--bars">
                  {monthlyQuery.isLoading && (
                    <LoadingState label="Loading customer revenue…" variant="skeleton-block" />
                  )}
                  {monthlyQuery.isError && (
                    <ErrorState
                      message="Could not load customer revenue."
                      onRetry={() => void monthlyQuery.refetch()}
                    />
                  )}
                  {monthlyQuery.data && (
                    <CustomerRevenueBars
                      data={monthlyQuery.data}
                      selectedCustomerIds={customerIds}
                      onSelectionChange={setCustomerSelection}
                      onClearSelection={customerIds.length > 0 ? clearCustomerFilter : undefined}
                    />
                  )}
                  {!monthlyQuery.isLoading && !monthlyQuery.data && !monthlyQuery.isError && (
                    <EmptyState
                      title="Select a line to edit"
                      description="Choose an opportunity from the list, or create a new one."
                      action={
                        <button
                          type="button"
                          className="button button--primary"
                          onClick={startCreate}
                        >
                          <Plus size={16} aria-hidden />
                          Create opportunity
                        </button>
                      }
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className="split-pane__detail-header split-pane__detail-header--compact">
                    <span className="visually-hidden">{detailTitle}</span>
                    <IconButton label="Close detail" variant="ghost" onClick={closeDetail}>
                      <X size={18} strokeWidth={2} />
                    </IconButton>
                  </div>

                  <div className="split-pane__detail-body">
                    {showDetailLoading && (
                      <LoadingState label="Loading opportunity details…" variant="skeleton-block" />
                    )}

                    {showDetailError && (
                      <ErrorState
                        message={
                          detailQuery.error instanceof ApiError
                            ? detailQuery.error.message
                            : 'Could not load opportunity.'
                        }
                        onRetry={() => void detailQuery.refetch()}
                      />
                    )}

                    {showDetailForm && (
                      <OpportunityForm
                        key={creating ? 'create' : (selectedId ?? 'detail')}
                        opportunity={creating ? null : editing}
                        onDirtyChange={setDirty}
                        onConfirmBlockedChange={setConfirmBlocked}
                        onSaved={afterSave}
                      />
                    )}
                  </div>

                  <div className="split-pane__detail-footer">
                    {finance && selectedId && !creating && (
                      <button
                        type="button"
                        className="button button--ghost"
                        style={{ marginRight: 'auto' }}
                        disabled={removeMutation.isPending}
                        onClick={handleRemove}
                      >
                        <Trash2 size={16} aria-hidden />
                        Remove
                      </button>
                    )}
                    <button type="button" className="button button--ghost" onClick={closeDetail}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      form={OPPORTUNITY_FORM_ID}
                      className="button button--primary"
                      disabled={
                        confirmBlocked ||
                        showDetailLoading ||
                        showDetailError ||
                        (ownerOnly &&
                          !creating &&
                          Boolean(editing) &&
                          editing?.owner_id !== user?.id)
                      }
                      title={
                        ownerOnly && !creating && editing && editing.owner_id !== user?.id
                          ? 'You can only edit opportunities you own.'
                          : undefined
                      }
                    >
                      Confirm change
                    </button>
                  </div>
                </>
              )}
            </FitScalePane>
          </div>
        </div>
      </HubPage>

      <ChatPanel
        open={aiAssistOpen}
        onClose={closeAiAssist}
        onOpenOpportunity={openOpportunityFromAi}
        onConfirmed={() => {
          void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
          void queryClient.invalidateQueries({ queryKey: ['forecast-summary'] })
          void queryClient.invalidateQueries({ queryKey: ['forecast-monthly'] })
        }}
        onActivityDone={() => setAiActivityBadge(true)}
      />

      <ConfirmDialog
        open={confirmKind === 'discard'}
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        danger
        onCancel={() => {
          setPendingNavigation(null)
          setConfirmKind(null)
        }}
        onConfirm={handleConfirmDialogConfirm}
      >
        Changes in this form have not been saved.
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmKind === 'remove'}
        title={`Remove ${removeLabel}?`}
        confirmLabel="Remove"
        danger
        busy={removeMutation.isPending}
        onCancel={() => setConfirmKind(null)}
        onConfirm={handleConfirmDialogConfirm}
      >
        It will be hidden from lists and totals.
      </ConfirmDialog>
    </>
  )
}

function SortButton({
  label,
  column,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string
  column: string
  sortBy: string
  sortDir: SortDir
  onSort: (column: string) => void
}) {
  const active = sortBy === column
  return (
    <button
      type="button"
      className={`table__sort${active ? ' table__sort--active' : ''}`}
      onClick={() => onSort(column)}
    >
      <span>{label}</span>
      {active ? (
        sortDir === 'asc' ? (
          <ArrowUp size={14} strokeWidth={2} aria-hidden />
        ) : (
          <ArrowDown size={14} strokeWidth={2} aria-hidden />
        )
      ) : (
        <ArrowUpDown size={14} strokeWidth={2} aria-hidden />
      )}
    </button>
  )
}

function lineRevenueForMetric(
  item: Opportunity,
  metric: ForecastMonthMetric,
  year: number,
  quarter: ForecastQuarter,
): number {
  if (quarter === 'all') {
    return lineYearTotalForMetric(item, metric)
  }
  return sumMetricForQuarter(
    opportunityMonthsToForecastAmounts(
      item.months,
      year,
      Number(item.probability ?? 0),
      item.stage_category,
    ),
    metric,
    quarter,
  )
}

function OpportunityRow({
  item,
  active,
  finance,
  selected,
  omitCustomer = false,
  monthMetric,
  year,
  quarter,
  showRemoved = false,
  onRestore,
  onToggleSelected,
  onSelect,
}: {
  item: Opportunity
  active: boolean
  finance: boolean
  selected: boolean
  omitCustomer?: boolean
  monthMetric: ForecastMonthMetric
  year: number
  quarter: ForecastQuarter
  showRemoved?: boolean
  onRestore?: (id: string) => void
  onToggleSelected: () => void
  onSelect: () => void
}) {
  const term = `${formatDate(item.start_date)} – ${formatDate(item.end_date)}`

  return (
    <tr className={active ? 'table__row--active' : undefined} onClick={onSelect}>
      {finance && (
        <td
          className="table__check-col"
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <input
            type="checkbox"
            aria-label={`Select ${item.name}`}
            checked={selected}
            onChange={onToggleSelected}
          />
        </td>
      )}
      {!omitCustomer && (
        <td className="table__sticky-col opp-col-customer">
          <span className="cell-stack__primary" title={item.customer_name?.trim() || undefined}>
            {item.customer_name?.trim() || '—'}
          </span>
        </td>
      )}
      <td
        className={
          omitCustomer
            ? 'table__sticky-col opp-col-opportunity'
            : 'table__sticky-col-2 opp-col-opportunity'
        }
      >
        <span className="cell-stack__primary" title={item.name}>
          {item.name}
          {showRemoved && item.is_removed ? (
            <span className="cell-stack__meta"> · Removed</span>
          ) : null}
        </span>
        {showRemoved && item.is_removed && onRestore ? (
          <button
            type="button"
            className="button button--ghost button--xs"
            onClick={(event) => {
              event.stopPropagation()
              onRestore(item.id)
            }}
          >
            Restore
          </button>
        ) : null}
      </td>
      <td className="opp-col-stage">
        <div className="stage-cell">
          <StageBadge name={item.stage_name} category={item.stage_category} />
          <span className="stage-cell__prob">
            <Probability value={item.probability} />
          </span>
        </div>
      </td>
      <td className="opp-col-term">
        <span className="cell-stack__primary" title={term}>
          {term}
        </span>
      </td>
      <td className="table__num opp-col-revenue">
        <Money
          amount={lineRevenueForMetric(item, monthMetric, year, quarter)}
          currency={item.currency}
        />
      </td>
    </tr>
  )
}
